import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { Attraction } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';
import { BilingualArrayField } from '../../components/BilingualArrayField';

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const ATTRACTION_TYPES = ['beach', 'nature', 'cultural', 'food', 'waterfall', 'hotspring'] as const;

const TYPE_LABELS: Record<string, string> = {
  beach: 'Beach',
  nature: 'Nature',
  cultural: 'Cultural',
  food: 'Food',
  waterfall: 'Waterfall',
  hotspring: 'Hot Spring',
};

export function AttractionsList() {
  const { data, isLoading, error } = useList<Attraction>({ resource: 'attractions' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this attraction?')) {
      deleteOne({ resource: 'attractions', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading attractions: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Attractions</h1>
        <button onClick={() => create('attractions')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Attraction
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((attraction) => (
              <tr key={attraction.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{attraction.name}</div>
                  <div className="text-sm text-gray-500">{attraction.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{TYPE_LABELS[attraction.type] || attraction.type}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{attraction.distance || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('attractions', attraction.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(attraction.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No attractions found. Click "Add Attraction" to create one.</div>}
      </div>
    </div>
  );
}

// Create Attraction
export function AttractionsCreate() {
  const navigate = useNavigate();
  const { mutate: createAttraction, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    directionsEn: '',
    directionsId: '',
    type: 'nature' as typeof ATTRACTION_TYPES[number],
    distance: '',
    location: { lat: 0, lng: 0 },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameId) { alert('Please fill in the name'); return; }
    const slug = form.slug || generateSlug(form.nameId);
    createAttraction(
      { resource: 'attractions', values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn, attractionType: form.type } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/attractions/edit/${newId}` : '/attractions'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/attractions')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Attraction</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new nearby attraction</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as typeof ATTRACTION_TYPES[number] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                {ATTRACTION_TYPES.map((type) => (<option key={type} value={type}>{TYPE_LABELS[type]}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
              <input type="text" value={form.distance} onChange={(e) => setForm({ ...form, distance: e.target.value })} placeholder="e.g., 15 km" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add images after creating the attraction.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/attractions')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Attraction
export function AttractionsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<Attraction>({ resource: 'attractions', id: id! });
  const { mutate: updateAttraction, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    directionsEn: '',
    directionsId: '',
    type: 'nature' as typeof ATTRACTION_TYPES[number],
    distance: '',
    location: { lat: 0, lng: 0 },
    tips: [] as { en: string; id: string }[],
  });
  const [images, setImages] = useState<ManagedImage[]>([]);

  useEffect(() => {
    if (data?.data) {
      const a = data.data;
      const mapArray = (arr: any[] | undefined) => arr ? arr.map((item: any) => typeof item === 'string' ? { en: '', id: item } : { en: item.en || '', id: item.id || '' }) : [];
      setForm({
        slug: a.slug || '',
        nameEn: (a as any).nameEn || (a as any).name_en || a.name || '',
        nameId: (a as any).nameId || (a as any).name_id || a.name || '',
        descriptionEn: (a as any).descriptionEn || (a as any).description_en || a.description || '',
        descriptionId: (a as any).descriptionId || (a as any).description_id || a.description || '',
        shortDescriptionEn: (a as any).shortDescriptionEn || (a as any).short_description_en || a.shortDescription || '',
        shortDescriptionId: (a as any).shortDescriptionId || (a as any).short_description_id || a.shortDescription || '',
        directionsEn: (a as any).directionsEn || (a as any).directions_en || a.directions || '',
        directionsId: (a as any).directionsId || (a as any).directions_id || a.directions || '',
        type: a.type || 'nature',
        distance: a.distance || '',
        location: a.location || { lat: 0, lng: 0 },
        tips: mapArray(a.tips),
      });
      if (a.images) {
        setImages(a.images.map((url, index) => ({ url, imageType: 'gallery' as const, sortOrder: index })));
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateAttraction(
      { resource: 'attractions', id: id!, values: { ...form, attractionType: form.type, images: images.map((img) => img.url) } },
      { onSuccess: () => navigate('/attractions') }
    );
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/attractions')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Attraction</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Attraction details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" value={form.slug} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50" readOnly />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as typeof ATTRACTION_TYPES[number] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                  {ATTRACTION_TYPES.map((type) => (<option key={type} value={type}>{TYPE_LABELS[type]}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
                <input type="text" value={form.distance} onChange={(e) => setForm({ ...form, distance: e.target.value })} placeholder="e.g., 15 km" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>
            <BilingualField label="Short Description" valueEn={form.shortDescriptionEn} valueId={form.shortDescriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })} multiline rows={2} />
            <BilingualField label="Full Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={4} />
            <BilingualField label="Directions" valueEn={form.directionsEn} valueId={form.directionsId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'directionsEn' : 'directionsId']: value })} multiline rows={2} placeholder="How to get there from the hotel..." />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Location</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                <input type="number" step="any" value={form.location.lat} onChange={(e) => setForm({ ...form, location: { ...form.location, lat: parseFloat(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                <input type="number" step="any" value={form.location.lng} onChange={(e) => setForm({ ...form, location: { ...form.location, lng: parseFloat(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Visitor Tips</h2>
            <BilingualArrayField label="Tips" items={form.tips} onChange={(tips) => setForm({ ...form, tips })} />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/attractions')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Attraction Images</h2>
            <ImageManager entityType="attraction" entityId={id!} images={images} imageType="gallery" multiple={true} maxImages={10} onChange={setImages} />
          </div>
        </div>
      </div>
    </div>
  );
}
